"use client";

import React from "react";
import { Maximize2, MapPin, Wrench } from "lucide-react";
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const projects = [{
  id: 1,
  src: "https://fivestarsroofline.com/wp-content/uploads/2026/03/628167656_1495323045927459_6891382986973396176_n.jpg?v=j904hdlb21",
  alt: "Premium EPDM rubber roofing installation by Five Star Roofline",
  location: "Newcastle Installation",
  materials: "Premium EPDM Rubber",
  heightClass: "h-[340px]"
}, {
  id: 2,
  src: "https://fivestarsroofline.com/wp-content/uploads/2026/03/626529845_1495323095927454_6784815751320281840_n-1-1.jpg?v=dv5d4pkkqfm",
  alt: "Full uPVC roofline transformation in the North East",
  location: "Durham Residence",
  materials: "Architectural uPVC",
  heightClass: "h-[480px]"
}, {
  id: 3,
  src: "https://fivestarsroofline.com/wp-content/uploads/2026/03/637701234_1508805021245928_3889829010141165144_n.jpg?v=qljakwz1m4",
  alt: "Advanced dry-verge system installation",
  location: "Gateshead Project",
  materials: "Dry-Verge Systems",
  heightClass: "h-[280px]"
}, {
  id: 4,
  src: "https://fivestarsroofline.com/wp-content/uploads/2026/03/638316596_1508805077912589_6049308714775291104_n.jpg?v=flbkt0djbin",
  alt: "dry verge",
  location: "Middlesbrough Site",
  materials: "Dry-Ridge & Guttering",
  heightClass: "h-[520px]"
}, {
  id: 5,
  src: "https://fivestarsroofline.com/wp-content/uploads/2026/03/628287559_1495322992594131_8742454672351491984_n.jpg?v=s7uuwpfzrfi",
  alt: "High-performance EPDM flat roof solution",
  location: "Sunderland Flat Roof",
  materials: "Grade-A EPDM",
  heightClass: "h-[380px]"
}, {
  id: 6,
  src: "https://fivestarsroofline.com/wp-content/uploads/2026/03/638202480_1508804957912601_5047779760426296932_n.jpg?v=029d9vp2c16t",
  alt: "Complete fascia and soffit aesthetic upgrade",
  location: "Darlington Upgrade",
  materials: "Fascia & Soffit Suite",
  heightClass: "h-[420px]"
}];
export default function RecentTransformations() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="124876" id="recent-project-gallery" className="py-24 bg-background text-foreground overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={ref} className={cn("max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold tracking-tight mb-6 text-foreground">
            Elite Project Gallery
          </h2>
          <p className="text-lg text-muted-foreground font-sans">
            Witness the Five Star standard in every detail. From high-performance EPDM rubber roofing to precision uPVC roofline transformations, our work across the North East defines professional excellence.
          </p>
        </div>

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 space-y-6">
          {projects.map((project, i) => <div key={project.id} data-index={i} className={cn("break-inside-avoid relative group rounded-xl overflow-hidden bg-muted transition-all duration-700 ease-out border border-border/50", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")} style={{
          transitionDelay: `${i * 100}ms`
        }}>
              <Dialog>
                <DialogTrigger asChild>
                  <button className="w-full text-left cursor-zoom-in block outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-xl" aria-label={`View Five Star project in ${project.location}`}>
                    <img src={project.src} alt={project.alt} className={cn("w-full object-cover transition-all duration-700 group-hover:scale-105", project.heightClass)} loading="lazy" />
                    
                    {}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                      <div className="translate-y-4 group-hover:translate-y-0 transition-transform duration-300 ease-out">
                        <h3 className="text-white font-serif font-semibold text-xl flex items-center gap-2 mb-2">
                          <MapPin className="size-5 text-primary shrink-0" />
                          <span className="truncate">{project.location}</span>
                        </h3>
                        <p className="text-primary font-sans text-sm flex items-center gap-2 font-medium tracking-wide uppercase">
                          <Wrench className="size-4 text-primary shrink-0" />
                          <span className="truncate">{project.materials}</span>
                        </p>
                      </div>
                      
                      {}
                      <div className="absolute top-6 right-6 bg-primary/20 backdrop-blur-md p-2.5 rounded-lg text-primary opacity-0 group-hover:opacity-100 transition-all duration-300 delay-100 scale-90 group-hover:scale-100 border border-primary/30 shadow-lg">
                        <Maximize2 className="size-5" />
                      </div>
                    </div>
                  </button>
                </DialogTrigger>
                
                <DialogContent className="max-w-5xl w-[95vw] p-0 overflow-hidden bg-background/10 backdrop-blur-2xl border-white/10 shadow-2xl rounded-xl">
                  <DialogTitle className="sr-only">{project.location}</DialogTitle>
                  <DialogDescription className="sr-only">{project.materials}</DialogDescription>
                  <div className="relative w-full h-full flex items-center justify-center bg-black/95">
                    <img src={project.src} alt={project.alt} className="w-full h-auto max-h-[85vh] object-contain" />
                    <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-black/95 to-transparent pointer-events-none">
                      <h4 className="text-white text-2xl font-serif font-bold flex items-center gap-3">
                        <MapPin className="size-6 text-primary" />
                        {project.location}
                      </h4>
                      <p className="text-primary mt-2 flex items-center gap-2 font-sans font-medium tracking-wide uppercase">
                        <Wrench className="size-5 text-primary" />
                        {project.materials}
                      </p>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>)}
        </div>
      </div>
    </section>;
}